import React, { useMemo } from 'react';
import * as THREE from 'three';

interface TowerModelProps {
  height: number; // meters
  crossMemberWidth: number; // mm
  sectionHeight: number; // meters (default 3m)
  shape: 'square' | 'triangle'; // tower shape
}

const TowerModel: React.FC<TowerModelProps> = ({ height, crossMemberWidth, sectionHeight = 3, shape = 'square' }) => {
  // Convert mm to meters for 3D
  const width = crossMemberWidth / 1000; // meters
  const cornerRadius = width / 2;
  
  // IMPORTANT: Use the same radius as mounting points to ensure alignment
  // Mounting points use towerRadius = 0.3 meters
  const TOWER_RADIUS = 0.3; // meters - must match MountingPoints.tsx

  // Create tower structure geometry based on sections
  // Each edge will have [x1, y1, z1, x2, y2, z2, sectionIndex]
  const towerLines = useMemo(() => {
    const edges: number[][] = [];
    
    // Calculate section boundaries
    const sections: number[] = [];
    let currentHeight = 0;
    while (currentHeight < height) {
      sections.push(currentHeight);
      currentHeight += sectionHeight;
      // Stop if we would exceed height
      if (currentHeight > height) {
        currentHeight = height;
      }
    }
    // Always add the final height as the last section boundary
    if (sections.length === 0 || sections[sections.length - 1] < height) {
      sections.push(height);
    }
    // Ensure we don't exceed the exact height
    if (sections[sections.length - 1] > height) {
      sections[sections.length - 1] = height;
    }

    let corners: number[][];
    
    if (shape === 'triangle') {
      // Triangle: 3 corners at 0°, 120°, 240° (equilateral triangle)
      // MUST match the mounting point positioning exactly
      // Mounting points use: x = Math.cos(angle) * towerRadius, z = Math.sin(angle) * towerRadius
      const angle0 = (0 * Math.PI) / 180;
      const angle120 = (120 * Math.PI) / 180;
      const angle240 = (240 * Math.PI) / 180;
      
      corners = [
        [Math.cos(angle0) * TOWER_RADIUS, Math.sin(angle0) * TOWER_RADIUS], // 0° - x=0.3, z=0
        [Math.cos(angle120) * TOWER_RADIUS, Math.sin(angle120) * TOWER_RADIUS], // 120°
        [Math.cos(angle240) * TOWER_RADIUS, Math.sin(angle240) * TOWER_RADIUS]  // 240°
      ];
    } else {
      // Square: 4 corners
      // Mounting points for square are at 0°, 90°, 180°, 270° with radius 0.3
      const angle0 = (0 * Math.PI) / 180;
      const angle90 = (90 * Math.PI) / 180;
      const angle180 = (180 * Math.PI) / 180;
      const angle270 = (270 * Math.PI) / 180;
      
      corners = [
        [Math.cos(angle0) * TOWER_RADIUS, Math.sin(angle0) * TOWER_RADIUS],   // 0° - N
        [Math.cos(angle90) * TOWER_RADIUS, Math.sin(angle90) * TOWER_RADIUS],  // 90° - E
        [Math.cos(angle180) * TOWER_RADIUS, Math.sin(angle180) * TOWER_RADIUS], // 180° - S
        [Math.cos(angle270) * TOWER_RADIUS, Math.sin(angle270) * TOWER_RADIUS]  // 270° - W
      ];
    }

    // Vertical edges - ONLY between section boundaries (not continuous full height)
    for (let i = 0; i < sections.length - 1; i++) {
      const bottomHeight = sections[i];
      const topHeight = sections[i + 1];
      
      // Create vertical posts for each corner, only between this section's boundaries
      corners.forEach(([x, z]) => {
        edges.push([x, bottomHeight, z, x, topHeight, z, i]); // Added section index
      });
    }

    // Cross-members at each section boundary
    sections.forEach((sectionBoundary, sectionIndex) => {
      if (shape === 'triangle') {
        // Triangle cross-members (3 sides) - use the same corners array
        // Connect corners in order: p1->p2, p2->p3, p3->p1
        const [p1, p2, p3] = corners;
        edges.push([p1[0], sectionBoundary, p1[1], p2[0], sectionBoundary, p2[1], sectionIndex]);
        edges.push([p2[0], sectionBoundary, p2[1], p3[0], sectionBoundary, p3[1], sectionIndex]);
        edges.push([p3[0], sectionBoundary, p3[1], p1[0], sectionBoundary, p1[1], sectionIndex]);
      } else {
        // Square cross-members (4 sides)
        const [p1, p2, p3, p4] = corners;
        edges.push([p1[0], sectionBoundary, p1[1], p2[0], sectionBoundary, p2[1], sectionIndex]);
        edges.push([p2[0], sectionBoundary, p2[1], p3[0], sectionBoundary, p3[1], sectionIndex]);
        edges.push([p3[0], sectionBoundary, p3[1], p4[0], sectionBoundary, p4[1], sectionIndex]);
        edges.push([p4[0], sectionBoundary, p4[1], p1[0], sectionBoundary, p1[1], sectionIndex]);
        
        // Diagonal braces (optional - creates more realistic lattice)
        edges.push([p1[0], sectionBoundary, p1[1], p3[0], sectionBoundary, p3[1], sectionIndex]);
        edges.push([p2[0], sectionBoundary, p2[1], p4[0], sectionBoundary, p4[1], sectionIndex]);
      }
    });

    return edges;
  }, [height, width, sectionHeight, shape]);

  return (
    <group>
      {towerLines.map((edge, index) => {
        const [x1, y1, z1, x2, y2, z2, sectionIndex] = edge;
        // Calculate distance and direction for the line
        const start = new THREE.Vector3(x1, y1, z1);
        const end = new THREE.Vector3(x2, y2, z2);
        const length = start.distanceTo(end);
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        
        // Create a cylinder to represent the line with thickness
        const radius = 0.03; // 3cm thick lines
        const geometry = new THREE.CylinderGeometry(radius, radius, length, 8);
        
        // Rotate cylinder to align with line direction
        const quaternion = new THREE.Quaternion();
        const up = new THREE.Vector3(0, 1, 0);
        quaternion.setFromUnitVectors(up, direction);
        
        // Alternate colors between sections: Red and White
        const color = sectionIndex % 2 === 0 ? '#ef4444' : '#ffffff'; // red-500 : white
        
        return (
          <mesh key={index} position={center} quaternion={quaternion} geometry={geometry}>
            <meshStandardMaterial color={color} />
          </mesh>
        );
      })}
    </group>
  );
};

export default React.memo(TowerModel);
